import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicApi, Demonstration, getMediaUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import DemonstrationUploadModal from '../components/DemonstrationUploadModal';
import { SkeletonDetail } from '../components/Skeleton';
import VideoPlayer from '../components/VideoPlayer';
import DemoWelcome from '../components/DemoWelcome';
import { useDemo } from '../context/DemoContext';

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const tenant = useTenant();
  const { accounts: demoAccounts } = useDemo();
  // Logged-out visitor on the public demo studio: show the role picker
  const isDemoVisitor = !!demoAccounts && !user;
  const [featuredDemo, setFeaturedDemo] = useState<Demonstration | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const canDemonstrate = !!user?.dancerId || !!user?.instructorId;

  useEffect(() => {
    const loadFeatured = async () => {
      try {
        const demo = await publicApi.demonstrations.getFeatured();
        setFeaturedDemo(demo);
      } catch (error) {
        console.error('Failed to load featured demonstration:', error);
      } finally {
        setLoading(false);
      }
    };
    loadFeatured();
  }, []);

  const getVideoUrl = (url: string) => getMediaUrl(url);

  return (
    <div className="home-page">
      {isDemoVisitor ? (
        <DemoWelcome />
      ) : (
        <h1>{tenant ? `Welcome to ${tenant.name}` : 'Welcome'}</h1>
      )}

      {/* Move/Pattern of the Week */}
      {loading && <SkeletonDetail />}
      {!loading && featuredDemo && (() => {
        const isPattern = featuredDemo.demonstrationType === 'PATTERN';
        const parent = isPattern ? featuredDemo.pattern : featuredDemo.move;
        const parentTitle = isPattern ? featuredDemo.pattern?.title : featuredDemo.move?.title;
        const detailHref = isPattern
          ? (featuredDemo.pattern ? `/library/patterns/${featuredDemo.pattern.slug || featuredDemo.pattern.id}` : '/library')
          : (featuredDemo.move?.id ? `/library/moves/${featuredDemo.move.id}` : '/library/moves');
        const sectionLabel = isPattern ? 'Pattern of the Week' : 'Move of the Week';
        const learnLabel = isPattern ? 'Learn This Pattern' : 'Learn This Move';
        const demoLabel = isPattern ? 'Demonstrate This Pattern' : 'Demonstrate This Move';
        const fallbackTitle = isPattern ? 'Dance Pattern' : 'Dance Move';
        const canShowDemoButton = canDemonstrate && !!parent;

        return (
          <div className="featured-move-section">
            <div className="featured-move-header">
              <h2>{sectionLabel}</h2>
            </div>
            <div className="featured-move-content">
              <div className="featured-move-video">
                <VideoPlayer
                  src={getVideoUrl(featuredDemo.videoUrl)}
                  poster={featuredDemo.thumbnail ? getVideoUrl(featuredDemo.thumbnail) : undefined}
                  title="Move of the week"
                  loop
                />
              </div>
              <div className="featured-move-info">
                <h3>
                  {parent ? (
                    <Link to={detailHref} className="home-featured-title-link">
                      {parentTitle}
                    </Link>
                  ) : (
                    fallbackTitle
                  )}
                  {' '}<span className="badge">Featured</span>
                </h3>
                <p>Demonstrated by <strong>{featuredDemo.dancer?.name}</strong></p>
                {featuredDemo.caption && (
                  <p className="home-featured-caption">"{featuredDemo.caption}"</p>
                )}
                <div className="featured-move-meta">
                  {parent?.style && <span>{parent.style.name}</span>}
                  {parent?.skillLevel && <span>{parent.skillLevel.name}</span>}
                </div>
                <div className="home-featured-actions">
                  <Link
                    to={detailHref}
                    className="home-featured-cta"
                  >
                    {learnLabel}
                  </Link>
                  {canShowDemoButton && (
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="home-featured-cta-outline"
                    >
                      {demoLabel}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Quick Links */}
      <div className="video-grid">
        <Link to="/library" className="video-card home-quick-link">
          <h3>Start Learning</h3>
          <p className="description">
            Explore our collection of salsa moves, from beginner basics to advanced combinations.
          </p>
        </Link>
        <Link to="/profile" className="video-card home-quick-link">
          <h3>Track Your Progress</h3>
          <p className="description">
            Uploading demonstrations provides a way for you to analyze where you are at. Compare yourself doing the same move after a few months and watch your growth.
          </p>
        </Link>
        <Link to="/profile" className="video-card home-quick-link">
          <h3>Request Feedback</h3>
          <p className="description">
            Publish your demonstration for dancers, instructors or both to give feedback.
          </p>
        </Link>
      </div>

      {/* Upload Demonstration Modal */}
      {showUploadModal && featuredDemo && (
        featuredDemo.demonstrationType === 'PATTERN' && featuredDemo.pattern ? (
          <DemonstrationUploadModal
            pattern={featuredDemo.pattern}
            onClose={() => setShowUploadModal(false)}
          />
        ) : featuredDemo.move ? (
          <DemonstrationUploadModal
            move={featuredDemo.move}
            onClose={() => setShowUploadModal(false)}
          />
        ) : null
      )}
    </div>
  );
};

export default HomePage;